<template>
  <div class="whatsapp-inbox-selector mt-6">
    <div class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-inbox" class="text-green-500 mt-0.5" size="20" />
        <div class="flex-1">
          <h6 class="font-medium text-green-900 dark:text-green-100 mb-2">
            Selecciona tu canal de WhatsApp
          </h6>
          <p class="text-sm text-green-800 dark:text-green-200 mb-4">
            Elige el canal de WhatsApp desde el cual enviarás los mensajes. Solo se mostrarán las plantillas aprobadas de este canal.
          </p>
          
          <!-- Loading State -->
          <div v-if="isLoading" class="flex items-center justify-center py-4">
            <Icon icon="i-lucide-loader-2" class="animate-spin text-green-500 mr-2" size="16" />
            <span class="text-sm text-green-800 dark:text-green-200">Cargando canales...</span>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
          </div>

          <!-- No Inboxes -->
          <div v-else-if="whatsappInboxes.length === 0" class="text-center py-4">
            <Icon icon="i-lucide-inbox" class="text-green-400 mb-2" size="32" />
            <p class="text-sm text-green-800 dark:text-green-200 font-medium">
              No hay canales de WhatsApp configurados
            </p>
            <p class="text-xs text-green-700 dark:text-green-300 mt-1">
              Configura al menos un canal de WhatsApp para usar esta funcionalidad
            </p>
          </div>

          <!-- Single Inbox (Auto-select) -->
          <div v-else-if="whatsappInboxes.length === 1" class="p-3 bg-white dark:bg-slate-800 border border-green-300 dark:border-green-700 rounded-md">
            <div class="flex items-center space-x-3">
              <div class="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                <Icon icon="i-lucide-message-circle" class="text-green-600 dark:text-green-400" size="20" />
              </div>
              <div class="flex-1">
                <h6 class="font-medium text-slate-900 dark:text-white">
                  {{ whatsappInboxes[0].name }}
                </h6>
                <p class="text-sm text-slate-600 dark:text-slate-400">
                  {{ getInboxTemplateCount(whatsappInboxes[0]) }} plantillas disponibles
                </p>
              </div>
              <Icon icon="i-lucide-check-circle" class="text-green-500" size="20" />
            </div>
          </div>

          <!-- Multiple Inboxes (Selector) -->
          <div v-else class="space-y-3">
            <div class="grid grid-cols-1 gap-3">
              <button
                v-for="inbox in whatsappInboxes"
                :key="inbox.id"
                @click="selectInbox(inbox)"
                :class="[
                  'p-3 border rounded-md text-left transition-all group',
                  selectedInbox?.id === inbox.id
                    ? 'border-green-500 bg-white dark:bg-slate-800 ring-2 ring-green-200 dark:ring-green-800'
                    : 'border-green-200 dark:border-green-700 bg-white dark:bg-slate-800 hover:border-green-300 hover:ring-1 hover:ring-green-200 dark:hover:ring-green-800'
                ]"
              >
                <div class="flex items-center space-x-3">
                  <div class="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                    <Icon icon="i-lucide-message-circle" class="text-green-600 dark:text-green-400" size="20" />
                  </div>
                  <div class="flex-1">
                    <h6 
                      :class="[
                        'font-medium',
                        selectedInbox?.id === inbox.id
                          ? 'text-green-900 dark:text-green-100'
                          : 'text-slate-900 dark:text-white group-hover:text-green-700 dark:group-hover:text-green-300'
                      ]"
                    >
                      {{ inbox.name }}
                    </h6>
                    <p class="text-sm text-slate-600 dark:text-slate-400">
                      {{ getInboxTemplateCount(inbox) }} plantillas disponibles
                    </p>
                  </div>
                  <Icon
                    v-if="selectedInbox?.id === inbox.id"
                    icon="i-lucide-check-circle"
                    class="text-green-500"
                    size="20"
                  />
                </div>
              </button>
            </div>

            <!-- Selected Inbox Info -->
            <div v-if="selectedInbox && whatsappInboxes.length > 1" class="mt-3 p-3 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-md">
              <div class="flex items-center space-x-2">
                <Icon icon="i-lucide-check-circle" class="text-green-600 dark:text-green-400" size="16" />
                <span class="text-sm font-medium text-green-800 dark:text-green-200">
                  Canal seleccionado: {{ selectedInbox.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:selectedInbox']);

const store = useStore();
const inboxesList = useMapGetter('inboxes/getInboxes');

const isLoading = ref(false);
const error = ref(null);

// Computed properties
const whatsappInboxes = computed(() => {
  return inboxesList.value.filter(inbox => 
    inbox.channel_type === 'Channel::Whatsapp'
  );
});

// Methods
const selectInbox = (inbox) => {
  emit('update:selectedInbox', inbox);
};

const getInboxTemplateCount = (inbox) => {
  if (!inbox.message_templates || !Array.isArray(inbox.message_templates)) {
    return 0;
  }
  return inbox.message_templates.filter(template => template.status === 'APPROVED').length;
};

const loadInboxes = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    await store.dispatch('inboxes/get');
  } catch (err) {
    error.value = 'Error al cargar los canales de WhatsApp';
    console.error('Error loading inboxes:', err);
  } finally {
    isLoading.value = false;
  }
};

// Auto-select if only one WhatsApp inbox
watch(whatsappInboxes, (newInboxes) => {
  if (newInboxes.length === 1 && !props.selectedInbox) {
    selectInbox(newInboxes[0]);
  }
}, { immediate: true });

onMounted(async () => {
  if (inboxesList.value.length === 0) {
    await loadInboxes();
  }
});
</script>